import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Car, Building, X } from 'lucide-react';
import api from '../../../api.js';

/**
 * VehicleAssignmentToDriverModal
 * Assigns a vehicle to a driver (from the driver detail view)
 *
 * Includes:
 * - Vehicle qualification validation (license categories, certifications)
 * - Organization filter (only vehicles from driver's organization)
 * - Current assignment conflict check
 */
function VehicleAssignmentToDriverModal({ isOpen, onClose, onSuccess, onMessage, driver, vehicles }) {
  const { t } = useTranslation();
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [purpose, setPurpose] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentAssignments, setCurrentAssignments] = useState([]);

  // Load current vehicle assignments to check for conflicts
  useEffect(() => {
    if (isOpen && driver) {
      loadCurrentAssignments();
    }
  }, [isOpen, driver]);

  const loadCurrentAssignments = async () => {
    try {
      // Check for existing active trips for this driver
      const tripsRes = await api.trip.getAll();
      const activeTrips = (tripsRes.data || []).filter(tr =>
        tr.driver_id === driver.id &&
        !tr.end_date &&
        (tr.status === 'in_progress' || tr.status === 'planned')
      );
      setCurrentAssignments(activeTrips);
    } catch (error) {
      console.error('Error loading assignments:', error);
    }
  };

  // Check if driver meets vehicle qualification requirements
  const checkVehicleQualifications = (vehicle) => {
    if (!vehicle || !driver) return { isQualified: false, missingQualifications: [] };

    const missing = [];

    // Check required license category
    const requiredLicense = vehicle.required_license_category || 'B';
    const licenseMap = {
      'B': 'license_category_b',
      'C': 'license_category_c',
      'D': 'license_category_d',
      'CE': 'license_category_ce'
    };

    const hasRequiredLicense = driver[licenseMap[requiredLicense]];
    if (!hasRequiredLicense) {
      missing.push(t('drivers.vehicleAssignmentModal.license', { category: requiredLicense }));
    }

    // Check certifications
    if (vehicle.requires_cert_adr && !driver.cert_adr) {
      missing.push(t('drivers.vehicleAssignmentModal.certAdr'));
    }
    if (vehicle.requires_cert_fimo && !driver.cert_fimo) {
      missing.push(t('drivers.vehicleAssignmentModal.certFimo'));
    }
    if (vehicle.requires_cert_crane && !driver.cert_crane) {
      missing.push(t('drivers.vehicleAssignmentModal.certCrane'));
    }
    if (vehicle.requires_cert_forklift && !driver.cert_forklift) {
      missing.push(t('drivers.vehicleAssignmentModal.certForklift'));
    }

    return {
      isQualified: missing.length === 0,
      missingQualifications: missing
    };
  };

  // Check if vehicle is already assigned to another driver
  const isVehicleAssigned = (vehicleId) => {
    // Check in current trips (any driver)
    return currentAssignments.some(tr => tr.vehicle_id === vehicleId && tr.driver_id !== driver.id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedVehicleId) {
      onMessage && onMessage({
        title: t('drivers.messages.errorTitle'),
        message: t('drivers.vehicleAssignmentModal.selectVehicleRequired'),
        type: 'error'
      });
      return;
    }

    const vehicle = vehicles.find(v => v.id === parseInt(selectedVehicleId));
    const quals = checkVehicleQualifications(vehicle);

    if (!quals.isQualified) {
      onMessage && onMessage({
        title: t('drivers.messages.errorTitle'),
        message: t('drivers.vehicleAssignmentModal.missingQualifications', { list: quals.missingQualifications.join(', ') }),
        type: 'error'
      });
      return;
    }

    setLoading(true);

    try {
      // Create a trip record to represent the assignment (consistent with DriverAssignmentModal)
      const tripData = {
        org_id: driver.org_id || vehicle.org_id,
        vehicle_id: parseInt(selectedVehicleId),
        driver_id: driver.id,
        start_date: startDate,
        start_mileage: vehicle.mileage || 0,
        purpose: purpose || 'Assignment',
        status: 'in_progress'
      };

      await api.trip.create(tripData);

      onMessage && onMessage({
        title: t('drivers.messages.successTitle'),
        message: t('drivers.vehicleAssignmentModal.assignSuccess', {
          plate: vehicle?.license_plate || '',
          name: `${driver.first_name} ${driver.last_name}`
        }),
        type: 'success'
      });

      // Reset form
      setSelectedVehicleId('');
      setPurpose('');

      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error('Error assigning vehicle:', error);
      onMessage && onMessage({
        title: t('drivers.messages.errorTitle'),
        message: t('drivers.vehicleAssignmentModal.assignError', { error: error.message || t('drivers.vehicleAssignmentModal.unknownError') }),
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Filter vehicles by driver's organization (if driver has one)
  const orgFilteredVehicles = driver?.org_id
    ? vehicles.filter(v => v.org_id === driver.org_id)
    : vehicles;

  // Only show active vehicles
  const activeVehicles = orgFilteredVehicles.filter(v => v.status === 'active');

  // Categorize vehicles by qualification status
  const qualifiedVehicles = activeVehicles.filter(v => {
    const quals = checkVehicleQualifications(v);
    return quals.isQualified && !isVehicleAssigned(v.id);
  });

  const unqualifiedVehicles = activeVehicles.filter(v => {
    const quals = checkVehicleQualifications(v);
    return !quals.isQualified;
  });

  const assignedVehicles = activeVehicles.filter(v => isVehicleAssigned(v.id));

  const hasOrganization = !!driver?.org_id;

  // Check if driver already has an active assignment
  const driverCurrentAssignment = currentAssignments.find(tr => tr.driver_id === driver?.id);

  const licenseTooltip = (withIssueDate) => t(
    withIssueDate ? 'drivers.vehicleAssignmentModal.licenseTooltipFull' : 'drivers.vehicleAssignmentModal.licenseTooltip',
    {
      number: driver?.license_number || '—',
      expiry: driver?.license_expiry || '—',
      issued: driver?.license_issue_date || '—'
    }
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-4 flex justify-between items-center rounded-t-lg sticky top-0">
          <h2 className="text-2xl font-bold">{t('drivers.vehicleAssignmentModal.title')}</h2>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Driver info */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">{t('drivers.vehicleAssignmentModal.driver')}</h3>
            <p className="text-gray-700">{driver?.first_name} {driver?.last_name}</p>
            <p className="text-sm text-gray-500">{driver?.email}</p>
            {driver?.org_name && (
              <p className="text-sm text-gray-600 mt-1">
                <Building className="w-4 h-4 inline mr-1" />
                {t('drivers.vehicleAssignmentModal.organization', { name: driver.org_name })}
              </p>
            )}

            {/* Show driver qualifications — use !! to avoid React rendering integer 0 as text */}
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-1">{t('drivers.vehicleAssignmentModal.driverQualifications')}</p>
              <div className="flex flex-wrap gap-2">
                {!!driver?.license_category_b && (
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded cursor-help group relative"
                    title={licenseTooltip(true)}
                  >{t('drivers.vehicleAssignmentModal.license', { category: 'B' })}</span>
                )}
                {!!driver?.license_category_c && (
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded cursor-help"
                    title={licenseTooltip(false)}
                  >{t('drivers.vehicleAssignmentModal.license', { category: 'C' })}</span>
                )}
                {!!driver?.license_category_d && (
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded cursor-help"
                    title={licenseTooltip(false)}
                  >{t('drivers.vehicleAssignmentModal.license', { category: 'D' })}</span>
                )}
                {!!driver?.license_category_ce && (
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded cursor-help"
                    title={licenseTooltip(false)}
                  >{t('drivers.vehicleAssignmentModal.license', { category: 'CE' })}</span>
                )}
                {!!driver?.cert_adr && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">ADR</span>
                )}
                {!!driver?.cert_fimo && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">FIMO</span>
                )}
                {!!driver?.cert_crane && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">{t('drivers.vehicleAssignmentModal.badgeCrane')}</span>
                )}
                {!!driver?.cert_forklift && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">{t('drivers.vehicleAssignmentModal.badgeForklift')}</span>
                )}
                {!driver?.license_category_b && !driver?.license_category_c && !driver?.license_category_d && !driver?.license_category_ce && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">{t('drivers.vehicleAssignmentModal.noLicense')}</span>
                )}
              </div>
            </div>
          </div>

          {/* Warning if no organization */}
          {!hasOrganization && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-900">{t('drivers.vehicleAssignmentModal.orgRequiredTitle')}</p>
                <p className="text-sm text-amber-800">
                  {t('drivers.vehicleAssignmentModal.orgRequiredText')}
                </p>
              </div>
            </div>
          )}

          {/* Warning if driver already has an assignment */}
          {driverCurrentAssignment && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">{t('drivers.vehicleAssignmentModal.activeAssignmentTitle')}</p>
                <p className="text-sm text-blue-800">
                  {t('drivers.vehicleAssignmentModal.activeAssignmentText')}
                </p>
              </div>
            </div>
          )}

          {/* Vehicle selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('drivers.vehicleAssignmentModal.vehicle')} *
            </label>

            {/* Show warning if there are unqualified vehicles */}
            {unqualifiedVehicles.length > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-3">
                <p className="text-sm text-orange-900 font-medium mb-1">
                  {t('drivers.vehicleAssignmentModal.unqualifiedCount', { count: unqualifiedVehicles.length })}
                </p>
                <ul className="text-xs text-orange-800 space-y-1 max-h-24 overflow-y-auto">
                  {unqualifiedVehicles.slice(0, 5).map(vehicle => {
                    const quals = checkVehicleQualifications(vehicle);
                    return (
                      <li key={vehicle.id}>
                        <strong>{vehicle.make} {vehicle.model}</strong> ({vehicle.license_plate}) - {t('drivers.vehicleAssignmentModal.missing', { list: quals.missingQualifications.join(', ') })}
                      </li>
                    );
                  })}
                  {unqualifiedVehicles.length > 5 && (
                    <li className="italic">{t('drivers.vehicleAssignmentModal.andOthers', { count: unqualifiedVehicles.length - 5 })}</li>
                  )}
                </ul>
              </div>
            )}

            {/* Show info about assigned vehicles */}
            {assignedVehicles.length > 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
                <p className="text-sm text-gray-700">
                  {t('drivers.vehicleAssignmentModal.assignedElsewhereCount', { count: assignedVehicles.length })}
                </p>
              </div>
            )}

            {activeVehicles.length > 0 && qualifiedVehicles.length === 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                <p className="text-sm text-red-800">
                  {t('drivers.vehicleAssignmentModal.noMatchingVehicle')}
                </p>
              </div>
            )}

            {activeVehicles.length === 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                <p className="text-sm text-blue-800">
                  {hasOrganization
                    ? t('drivers.vehicleAssignmentModal.noActiveVehicleInOrg', { org: driver.org_name })
                    : t('drivers.vehicleAssignmentModal.noActiveVehicle')}
                </p>
              </div>
            )}

            <select
              value={selectedVehicleId}
              onChange={(e) => setSelectedVehicleId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              required
              disabled={qualifiedVehicles.length === 0}
            >
              <option value="">
                {qualifiedVehicles.length === 0
                  ? t('drivers.vehicleAssignmentModal.noQualifiedVehicle')
                  : t('drivers.vehicleAssignmentModal.selectVehiclePlaceholder')}
              </option>
              {qualifiedVehicles.map(vehicle => (
                <option key={vehicle.id} value={vehicle.id}>
                  {vehicle.make} {vehicle.model} - {vehicle.license_plate}
                  {vehicle.required_license_category && vehicle.required_license_category !== 'B'
                    ? ` (${t('drivers.vehicleAssignmentModal.license', { category: vehicle.required_license_category })})`
                    : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Start date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('drivers.vehicleAssignmentModal.startDate')}
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          {/* Purpose/Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('drivers.vehicleAssignmentModal.purpose')}
            </label>
            <input
              type="text"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder={t('drivers.vehicleAssignmentModal.purposePlaceholder')}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
              disabled={loading || qualifiedVehicles.length === 0}
            >
              {loading ? (
                <>{t('drivers.vehicleAssignmentModal.assigning')}</>
              ) : (
                <>
                  <Car className="w-5 h-5" />
                  {t('drivers.vehicleAssignmentModal.assign')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default VehicleAssignmentToDriverModal;
